import { useEffect, useState } from "react";
import {
  add_transaction_fee,
  get_seller_by_id,
  verify_seller,
} from "../../reduxData/user/userAction";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import DefaultImg from "../../images/no_profile.png";
import { getCountryCallingCode } from "react-phone-number-input";
import SellerNGO from "./SellerNGO";
import useAttributes from "./Hooks/useAttributes";

const { REACT_APP_IMAGE_URL } = process.env;
const getImageUrl = (path) =>
  path ? `${REACT_APP_IMAGE_URL || ""}${path}` : DefaultImg;

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const safeCountryCode = (country) => {
  try {
    return country ? `+${getCountryCallingCode(country)}` : "";
  } catch {
    return "";
  }
};

const SellerDetails = () => {
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isNGOSeller, setIsNGOSeller] = useState(null);
  const [allowEditBrand, setAllowBrand] = useState(false);
  const [selectNGOAttributes, setSelectNGOAttributes] = useState({
    brand_id: "",
    product_type_id: "",
  });
  const token = useSelector((state) => state.auth.accessToken);
  const { brands, category, editBrand } = useAttributes();

  const [fee, setFee] = useState("");
  const { id } = useParams();
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    const res = await get_seller_by_id(id);
    setSeller(res?.data || null);
    if (res?.data?.transaction_fee != null) {
      setFee(Number(res?.data?.transaction_fee) ?? 10);
    }
    setIsNGOSeller(
      res?.data?.community_seller_status == "approved" ? true : false
    );
    setSelectNGOAttributes((prev) => ({
      ...prev,
      brand_id: res?.data?.brand_id,
    }));
    setAllowBrand(res?.data?.brand_id? true:false);
    setLoading(false);
  };

  const handleFee = async () => {
    const data = {
      transaction_fee: Number(fee),
    };
    const res = await add_transaction_fee(data, id);
    fetchData();
  };

  const handleVerified = async () => {
    const res = await verify_seller(id);
    if (res) {
      navigate("/seller");
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div className="container py-5">
        <div className="text-center py-5">
          <div
            className="spinner-border text-primary"
            role="status"
            style={{ width: "2.5rem", height: "2.5rem" }}
          />
          <p className="mt-3 text-muted small">Loading seller details...</p>
        </div>
      </div>
    );
  }

  if (!seller || !seller.id) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning d-flex align-items-center justify-content-between rounded-3">
          <span>Seller not found.</span>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => navigate(-1)}
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  const displayName =
    [seller.first_name, seller.last_name].filter(Boolean).join(" ") ||
    seller.user_name ||
    seller.email ||
    "Seller";

  const addr0 = seller.shipping_addresses?.[0];
  const phoneDisplay = addr0?.phoneNumber
    ? `${safeCountryCode(addr0.country)} ${addr0.phoneNumber}`.trim() ||
      addr0.phoneNumber
    : "—";

  return (
    <div className="container py-4">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div className="d-flex align-items-center gap-3">
          <button
            type="button"
            className="btn btn-light border d-flex align-items-center gap-2 rounded-3 px-3 py-2"
            onClick={() => navigate(-1)}
          >
            <i className="bi bi-arrow-left" />
            <span>Back</span>
          </button>
          <h4 className="mb-0 fw-600">Seller Details</h4>
        </div>
      </div>

      {/* Identity documents – prominent at top */}
      <div className="card border-0 shadow-sm rounded-4 mb-4 overflow-hidden">
        <div className="card-body py-4">
          <div className="d-flex align-items-center gap-2 mb-3">
            <span className="badge bg-primary bg-opacity-10 text-primary rounded-pill px-3 py-2">
              <i className="bi bi-card-image me-1" />
              Identity documents
            </span>
          </div>
          <div className="row g-4">
            <div className="col-md-6">
              <div
                className="rounded-3 overflow-hidden border bg-light d-flex align-items-center justify-content-center position-relative"
                style={{
                  minHeight: 200,
                  borderColor: "var(--bs-border-color)",
                }}
              >
                <span
                  className="position-absolute top-0 start-0 m-2 badge bg-dark bg-opacity-75 rounded-pill px-2 py-1 small"
                  style={{ zIndex: 1 }}
                >
                  Front
                </span>
                {seller.front_identity_image ? (
                  <img
                    src={getImageUrl(seller?.front_identity_image)}
                    alt="Front ID"
                    className="img-fluid"
                    style={{ maxHeight: 240, objectFit: "contain" }}
                    onError={(e) => {
                      e.target.src = DefaultImg;
                    }}
                  />
                ) : (
                  <span className="text-muted small">No image</span>
                )}
              </div>
            </div>
            <div className="col-md-6">
              <div
                className="rounded-3 overflow-hidden border bg-light d-flex align-items-center justify-content-center position-relative"
                style={{
                  minHeight: 200,
                  borderColor: "var(--bs-border-color)",
                }}
              >
                <span
                  className="position-absolute top-0 start-0 m-2 badge bg-dark bg-opacity-75 rounded-pill px-2 py-1 small"
                  style={{ zIndex: 1 }}
                >
                  Back
                </span>
                {seller?.back_identity_image ? (
                  <img
                    src={getImageUrl(seller?.back_identity_image)}
                    alt="Back ID"
                    className="img-fluid"
                    style={{ maxHeight: 240, objectFit: "contain" }}
                    onError={(e) => {
                      e.target.src = DefaultImg;
                    }}
                  />
                ) : (
                  <span className="text-muted small">No image</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4">
        {/* Profile */}
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm rounded-4 h-100 overflow-hidden">
            <div className="card-header bg-white border-0 py-3">
              <h6 className="mb-0 fw-600 text-dark">Profile</h6>
            </div>
            <div className="card-body">
              <div className="d-flex align-items-center gap-3 mb-4">
                <div
                  className="rounded-circle overflow-hidden border border-2 border-light shadow-sm d-flex align-items-center justify-content-center bg-light"
                  style={{ width: 64, height: 64 }}
                >
                  <img
                    src={
                      seller.profile_pic
                        ? getImageUrl(seller?.profile_pic)
                        : DefaultImg
                    }
                    alt=""
                    className="w-100 h-100 object-fit-cover"
                    onError={(e) => {
                      e.target.src = DefaultImg;
                    }}
                  />
                </div>
                <div className="flex-grow-1 min-w-0">
                  <h5 className="mb-0 fw-600 text-truncate">{displayName}</h5>
                  <p className="mb-0 text-muted small text-truncate">
                    {seller?.email || "—"}
                  </p>
                </div>
              </div>
              <div className="row g-3">
                <div className="col-6">
                  <p className="mb-0 small text-muted">Role</p>
                  <p className="mb-0 fw-medium">Seller</p>
                </div>
                <div className="col-6">
                  <p className="mb-0 small text-muted">Joined</p>
                  <p className="mb-0 fw-medium">
                    {formatDate(seller?.created_at)}
                  </p>
                </div>
                <div className="col-12">
                  <p className="mb-0 small text-muted">Phone</p>
                  <p className="mb-0 fw-medium">{phoneDisplay}</p>
                </div>
                <div className="col-6">
                  <p className="mb-0 small text-muted">
                    Add Transaction Fee (%)
                  </p>
                  <input
                    className="form-control"
                    placeholder="Enter Transaction Fee"
                    type="number"
                    value={fee}
                    onChange={(e) => setFee(e.target.value)}
                    min={1}
                    onKeyDown={(e) => {
                      if (["+", "-", " ", "e", "="].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                  <button
                    className="btn btn-primary mt-3 px-4 "
                    onClick={handleFee}
                    disabled={!fee}
                  >
                    Add
                  </button>
                </div>
                {seller.bio && (
                  <div className="col-12">
                    <p className="mb-0 small text-muted">Bio</p>
                    <p className="mb-0 fw-medium">{seller?.bio}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Status & verify */}
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm rounded-4 h-100 overflow-hidden">
            <div className="card-header bg-white border-0 py-3">
              <h6 className="mb-0 fw-600 text-dark">Status & actions</h6>
            </div>
            <div className="card-body d-flex flex-column justify-content-center">
              {!seller.admin_verified ? (
                <div className="text-center py-2">
                  <p className="text-muted small mb-3">
                    This seller is pending verification.
                  </p>
                  <button
                    type="button"
                    className="btn btn-success rounded-3 px-4 py-2 d-inline-flex align-items-center gap-2"
                    onClick={handleVerified}
                  >
                    <i className="bi bi-check-circle-fill" />
                    Verify seller
                  </button>
                </div>
              ) : (
                <div className="text-center py-2">
                  <div className="d-inline-flex align-items-center gap-2 text-success fw-medium">
                    <i className="bi bi-patch-check-fill fs-5" />
                    Seller is verified
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Seller NGO */}

        <SellerNGO
          isNGOSeller={isNGOSeller}
          setIsNGOSeller={setIsNGOSeller}
          setSelectNGOAttributes={setSelectNGOAttributes}
          selectNGOAttributes={selectNGOAttributes}
          brands={brands}
          category={category}
          fetchData={fetchData}
          id={id}
          seller={seller}
          editBrand={editBrand}
          setAllowBrand={setAllowBrand}
          allowEditBrand={allowEditBrand}
        
        />

        {/* Shipping addresses */}
        {Array.isArray(seller?.shipping_addresses) &&
          seller?.shipping_addresses?.length > 0 && (
            <div className="col-12">
              <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                <div className="card-header bg-white border-0 py-3">
                  <h6 className="mb-0 fw-600 text-dark">Shipping addresses</h6>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    {seller?.shipping_addresses?.map((addr, idx) => (
                      <div key={idx} className="col-md-6 col-lg-4">
                        <div className="border rounded-3 p-3 h-100 bg-light bg-opacity-50">
                          <p className="mb-2 fw-600 small">
                            {[addr?.first_name, addr?.last_name]
                              .filter(Boolean)
                              .join(" ") || "Address"}
                          </p>
                          <p className="mb-1 small text-muted">
                            {addr?.address}
                            {addr?.address2 ? `, ${addr?.address2}` : ""}
                          </p>
                          <p className="mb-0 small">
                            {[addr?.city, addr?.region, addr?.country]
                              .filter(Boolean)
                              .join(", ")}
                            {addr?.pincode ? ` ${addr?.pincode}` : ""}
                          </p>
                          {addr.phoneNumber && (
                            <p className="mb-0 small mt-2 text-muted">
                              {`${safeCountryCode(addr?.country)} ${
                                addr?.phoneNumber
                              }`.trim() || addr?.phoneNumber}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default SellerDetails;
